import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { 
  createBrowserRouter, 
  RouterProvider, 
  createRoutesFromElements,
  Route,
  Navigate
} from "react-router-dom";
import { useEffect } from "react";
import { useAuthStore } from "./lib/auth";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import Index from "./pages/Index";
import Registration from "./pages/Registration";
import Queue from "./pages/Queue";
import PatientView from "./pages/PatientView";

import History from "./pages/History";
import Settings from "./pages/Settings";
import Analytics from "./pages/Analytics";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Unauthorized from "./pages/Unauthorized";
import DoctorDashboard from "./pages/DoctorDashboard";
import EPrescription from "./pages/EPrescription";
import { ResetPassword } from "./pages/ResetPassword";
import { ConfirmEmail } from "./pages/ConfirmEmail";
import Landing from "./pages/Landing";
import RefundPolicy from "./pages/RefundPolicy";
import ContactUs from "./pages/ContactUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ShippingPolicy from "./pages/ShippingPolicy";
import UserGuide from "./pages/UserGuide";
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SignUp } from '@/components/auth/SignUp';
import { SaaSSignUp } from '@/components/auth/SaaS-SignUp';
import UserManagement from '@/pages/UserManagement';
import Subscription from '@/pages/Subscription';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import { selectiveRealtimeManager } from '@/lib/realtime';
import FollowUps from "@/pages/FollowUps";
import { AppWrapper } from '@/components/layout/AppWrapper';
import { useWallpaperStore } from '@/lib/stores/wallpaper';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => {
  const initialize = useAuthStore((state) => state.initialize);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const loadWallpaper = useWallpaperStore((state) => state.loadWallpaper);
  const fetchProfile = useHospitalProfileStore((state) => state.fetchProfile);
  
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupAuth = async () => {
      cleanup = await initialize();
    };

    setupAuth();

    // Cleanup on unmount
    return () => {
      if (cleanup) cleanup();
      selectiveRealtimeManager.cleanup();
    };
  }, [initialize]);

  // Initialize realtime subscriptions only after authentication is complete
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Small delay to ensure auth state is fully settled
      const timer = setTimeout(() => {
        selectiveRealtimeManager.initialize();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading]);

  // Initialize wallpaper when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const initializeWallpaper = async () => {
        await fetchProfile();
        loadWallpaper();
      };
      
      initializeWallpaper();
    }
  }, [isAuthenticated, isLoading, fetchProfile, loadWallpaper]);

  // Add effect to handle logout cleanup
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up realtime subscriptions when user logs out
      selectiveRealtimeManager.cleanup();
    }
  }, [isAuthenticated]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        {/* Public routes */}
        <Route path="/login" element={
          isLoading ? <div className="min-h-screen flex items-center justify-center">Loading...</div> :
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/signup" element={
          isLoading ? <div className="min-h-screen flex items-center justify-center">Loading...</div> :
          isAuthenticated ? <Navigate to="/" replace /> : <SaaSSignUp />
        } />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        
        {/* Policy Pages - Public Access (Required for Razorpay) */}
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/user-guide" element={<UserGuide />} />
        
        {/* Doctor routes */}
        <Route path="/doctor-dashboard" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['doctor']}>
              <DoctorDashboard />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/e-prescription/:id" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['doctor']}>
              <EPrescription />
            </ProtectedRoute>
          </AppWrapper>
        } />
        
        {/* Landing page for unauthenticated users */}
        <Route path="/" element={
          isLoading ? <div className="min-h-screen flex items-center justify-center">Loading...</div> :
          !isAuthenticated ? <Landing /> : (
            <AppWrapper>
              <ProtectedRoute>
                {user?.role === 'doctor' ? <Navigate to="/doctor-dashboard" replace /> : 
                 user?.role === 'super_admin' ? <Navigate to="/super-admin" replace /> : 
                 <Index />}
              </ProtectedRoute>
            </AppWrapper>
          )
        } />
        <Route path="/registration" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['staff']}>
              <Registration />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/queue" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <Queue />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/patient/:id" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin', 'staff', 'doctor']}>
              <PatientView />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/followups" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['staff']}>
              <FollowUps />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/analytics" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <Analytics />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/history" element={
          <AppWrapper>
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/settings" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <Settings />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/subscription" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <Subscription />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/users" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="/super-admin" element={
          <AppWrapper>
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          </AppWrapper>
        } />
        <Route path="*" element={<NotFound />} />
      </Route>
    ),
    {
      future: {
        v7_startTransition: true,
        v7_relativeSplatPath: true
      } as any
    }
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
